/**
 * View-transition wrapper for the module-pane open/close flip.
 *
 * The browser's View Transitions API does exactly what a hand-rolled flight
 * surface can't: it SNAPSHOTS the real pixels of the chat panel (messages,
 * composer, module card — anything with a view-transition-name in
 * ChatBox.css), applies the layout flip underneath, and morphs the old
 * image into the new live render. The user keeps seeing their actual chat
 * content the whole way — no hidden content, no empty traveling card, no
 * fade-from-blank at landing.
 *
 * `animate` is decided by the caller (user-intent write + desktop split +
 * no reduced-motion — the same gates the old flight used); hydration /
 * conversation-switch / auto-cleanup writes never come through here.
 * Unsupported browsers (no startViewTransition) get the plain instant flip.
 *
 * flushSync: startViewTransition captures "new" after its callback resolves,
 * so the React commit (including cascading useLayoutEffect updates like the
 * docked-exit's expandChatPane) must flush synchronously inside it.
 *
 * Rapid toggles are safe: starting a new transition while one is animating
 * makes the browser skip the old one to its end state.
 */
import { flushSync } from 'react-dom';

/**
 * `transitionClass` (optional) is added to <html> for the duration of the
 * transition and removed when it finishes, so CSS can give a specific flip its
 * own choreography — e.g. `vt-pane-closing` slides the module pane out to the
 * left instead of the default morph. Inert on the instant (non-animated) path.
 */
// Hard ceiling on how long a flip may hold the ::view-transition overlay. The
// longest healthy flip is the 0.5s vt-pane-closing morph (10-view-transitions.css),
// so 800ms clears it comfortably while bounding any stuck flip. See WATCHDOG below.
const VT_STUCK_DEADLINE_MS = 800;

// --- Mid-flip input rescue --------------------------------------------------
// The watchdog bounds a STUCK flip, but even a healthy flip holds the
// ::view-transition overlay for its full 180-500ms morph, and that overlay
// intercepts every pointer event (the `::view-transition{pointer-events:none}`
// opt-out is ineffective — see WATCHDOG note). Hit-testing during the morph
// resolves to <html>, so a rail/button press inside the window silently dies —
// the intermittent "clicking the sidebar does nothing". User input must beat
// decoration, in two parts (listeners registered once per document):
//   1. pointerdown while a flip is animating → skipTransition() immediately.
//      The overlay tears down, so the press's pointerup lands on live DOM.
//   2. The click synthesized from that press can still collapse to <html>
//      (a click's target is the common ancestor of its down/up targets), so a
//      click that lands on the bare document element right after a mid-flip
//      skip is re-dispatched to the interactive element under the pointer.
const RESCUE_WINDOW_MS = 1000;

// The single in-flight flip. `finalizeActive` is that flip's teardown (class
// release + watchdog clear + nulling); the recovery listeners force-skip and
// tear it down synchronously without waiting on `finished`, which a frozen tab
// may never resolve.
let activeTransition: ViewTransition | null = null;
let finalizeActive: (() => void) | null = null;
let rescueUntil = 0;
let recoveryListenersInstalled = false;

// The <html> transition class is reference-counted, not owned by one flip.
// Two overlapping flips can want the SAME class (both opening, both closing),
// and the OPPOSITE classes can be present at once during a rapid open→close.
// Removing on the first flip to settle would either strip a class its sibling
// still needs (visual hitch) or — with a naive `activeTransition === transition`
// guard — leak the opposite class forever (the grid-freeze rules keyed off it
// would then wedge). A count keyed by class value gets both right: the class
// leaves <html> only when the last flip using it settles.
const activeClassCounts = new Map<string, number>();

function retainTransitionClass(cls: string): void {
  const next = (activeClassCounts.get(cls) ?? 0) + 1;
  activeClassCounts.set(cls, next);
  if (next === 1) document.documentElement.classList.add(cls);
}

function releaseTransitionClass(cls: string): void {
  const next = (activeClassCounts.get(cls) ?? 0) - 1;
  if (next > 0) {
    activeClassCounts.set(cls, next);
    return;
  }
  activeClassCounts.delete(cls);
  document.documentElement.classList.remove(cls);
}

// Force-end the in-flight flip NOW and tear it down synchronously. Used by every
// recovery path (pointerdown, keyboard activation, tab-return) so the overlay
// clears immediately and the <html> class/watchdog never outlive a flip whose
// `finished` may be paused (backgrounded tab) — the watchdog is the last resort,
// not the only one.
function skipActiveTransition(): void {
  const transition = activeTransition;
  if (!transition) return;
  try {
    transition.skipTransition();
  } catch {
    // already finished/skipped — nothing to do
  }
  finalizeActive?.();
}

function installRecoveryListeners(): void {
  if (recoveryListenersInstalled) return;
  recoveryListenersInstalled = true;

  document.addEventListener('pointerdown', () => {
    if (!activeTransition) return;
    skipActiveTransition();
    rescueUntil = performance.now() + RESCUE_WINDOW_MS;
  }, true);

  document.addEventListener('click', (e) => {
    if (!rescueUntil || performance.now() > rescueUntil) return;
    // One press, one rescue: the first click after a mid-flip skip consumes
    // the window whether or not it needs forwarding.
    rescueUntil = 0;
    if (e.target !== document.documentElement) return; // landed on live DOM
    const under = document.elementFromPoint(e.clientX, e.clientY);
    const interactive = under?.closest<HTMLElement>(
      'button, a[href], [role="button"], input, select, textarea, summary, [tabindex]',
    );
    // Forward ONLY inside the sidebar rail — fixed chrome that does not
    // reflow during the pane flip. The flip's real DOM commits BEFORE the
    // morph plays (flushSync in flipPaneWithViewTransition), so inside the
    // reflowing chat/module region a coordinate-based forward could activate
    // a control the user never saw (the split reassigns up to ~540px of
    // width). There, the press already cleared the overlay via the
    // pointerdown skip — the user's next click lands normally on the final
    // layout they can now see.
    if (!interactive || !interactive.closest('.sidebar-rail')) return;
    // Suppress the original <html>-targeted event so document-level bubble
    // listeners see exactly ONE logical click — the synthetic one below,
    // which re-dispatches from the real target through React and any
    // outside-click listeners like an ordinary bubbled click.
    e.stopPropagation();
    interactive.click();
  }, true);

  // Keyboard-only activation (issue #5584). Enter/Space synthesizes a click
  // with NO preceding pointerdown, so the pointerdown rescue above never arms
  // for keyboard users — mid-flip they had only the watchdog. Force-skip on the
  // activation keydown so the overlay tears down before the synthesized click.
  // That click is keyboard-dispatched straight to the focused element (it has
  // no coordinate hit-test to collapse to <html>), so it needs no forwarding —
  // only an unobstructed target, which the skip provides.
  document.addEventListener('keydown', (e) => {
    if (!activeTransition) return;
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
    skipActiveTransition();
  }, true);

  // Backgrounded/frozen-tab recovery (issue #5584; mirrors useForegroundReload,
  // #3280/#5194). A tab hidden mid-flip may have its transition paused by the
  // browser, so `finished` never resolves and the watchdog's timer can't fire
  // until JS resumes — the app returns to the foreground click-dead until the
  // deadline. Proactively force-skip the instant we're visible again instead of
  // relying on the watchdog's implicit "the timer eventually fires" assumption.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    skipActiveTransition();
  });
}

export function flipPaneWithViewTransition(
  apply: () => void,
  animate: boolean,
  transitionClass?: string,
): void {
  // typeof guard doubles as the runtime feature check for browsers that
  // predate the API (lib.dom types it unconditionally).
  if (
    !animate
    || typeof document === 'undefined'
    || typeof document.startViewTransition !== 'function'
  ) {
    apply();
    return;
  }
  if (transitionClass) retainTransitionClass(transitionClass);
  const transition = document.startViewTransition(() => {
    flushSync(apply);
  });
  installRecoveryListeners();

  // finalize runs exactly once per flip (guarded by `settled`), whether it
  // fires from the watchdog, a recovery skip, or the natural `finished`
  // settlement. It releases this flip's class, clears its watchdog, and — only
  // if this flip is still the active one — nulls the shared active refs so a
  // newer overlapping flip that already took over is left intact.
  let settled = false;
  const finalize = () => {
    if (settled) return;
    settled = true;
    clearTimeout(watchdog);
    if (activeTransition === transition) {
      activeTransition = null;
      finalizeActive = null;
    }
    if (transitionClass) releaseTransitionClass(transitionClass);
  };

  activeTransition = transition;
  finalizeActive = finalize;

  // WATCHDOG (issue: "sidebar stuck until refresh"). While a view transition is
  // active, Chrome's ::view-transition overlay intercepts EVERY pointer event —
  // and `::view-transition { pointer-events: none }` does NOT opt it out (verified
  // ineffective in Chrome 149: the overlay still eats clicks for the whole flip).
  // Normally that window is ~180ms (open) / ~500ms (close). But if `finished`
  // never resolves — e.g. the tab is backgrounded mid-flip so the browser pauses
  // the animation, or the morph is otherwise interrupted — the overlay and the
  // <html> class persist and the entire app is click-dead until a page reload.
  // skipTransition() force-ends a stuck flip (overlay clears immediately); the
  // watchdog guarantees we can never be permanently frozen by one. The
  // visibilitychange recovery above beats it for the common backgrounded case.
  // finalize closes over this const; it never runs before the assignment below.
  const watchdog = setTimeout(() => {
    try {
      transition.skipTransition();
    } catch {
      // already finished/skipped — nothing to do
    }
    finalize();
  }, VT_STUCK_DEADLINE_MS);

  void transition.finished.finally(finalize);
}
